/**
 * Splat swap list helpers kept free of engine objects, so old scene shapes are unit-testable.
 */
import { ORIGINAL_SPLAT_URL, type SplatSwapAction, type SplatSwapPoint } from '../types';

/**
 * The scene's splat swaps without entries no swap code can read: a `null` slot (an `undefined`
 * array entry saved through JSON by an old or hand-edited scene) or anything that is not an
 * object. Every swap lookup reads `swap.url`, which would throw on such an entry; a list that is
 * not an array at all counts as no swaps.
 */
export function sanitizeSwapList(list: unknown): SplatSwapPoint[] {
    return Array.isArray(list) ? list.filter((s): s is SplatSwapPoint => !!s && typeof s === 'object') : [];
}

/**
 * The files a splat swap can load, best first.
 *
 * A swap's device URL set (already chosen as a whole for desktop or mobile, never mixed) is tried
 * in the primary splat's priority: LOD manifest > SOG > compressed PLY > original file. The first
 * is what loads; the rest are fallbacks, so a swap whose preferred file fails (an old scene whose
 * swap carries a lodMetaUrl it never loaded before chain / menu swaps resolved LOD first, a
 * deleted derivative, a manifest that does not parse) still shows its splat from the next file
 * instead of not showing at all. `fallbackUrl` (the swap's own url) is only used when the set is
 * empty: on a phone the desktop original is never loaded in place of the mobile set.
 */
export interface SwapUrlSet {
    lodMetaUrl?: string | null;
    sogUrl?: string | null;
    compressedPlyUrl?: string | null;
    splatUrl?: string | null;
}

export function swapUrlCandidates(selected: SwapUrlSet, fallbackUrl: string): string[] {
    const ordered = [selected.lodMetaUrl, selected.sogUrl, selected.compressedPlyUrl, selected.splatUrl];
    const candidates: string[] = [];
    for (const url of ordered) {
        if (typeof url === 'string' && url.trim() && !candidates.includes(url)) candidates.push(url);
    }
    return candidates.length > 0 ? candidates : [fallbackUrl];
}

/**
 * Whether a hand-picked swap target (a scene-menu item, a swap on click, goToSplat) names the main
 * splat: the ORIGINAL_SPLAT_URL marker, the main splat's file on screen (`primaryUrl`), or any other
 * file of it in `mainFiles` (the uploaded file, SOG, LOD manifest, mobile set, fallbacks) that no
 * splat swap uses as its url. A menu item's "Original / primary splat" saves the uploaded file,
 * while the viewer shows the scene's SOG or LOD manifest when it has one.
 */
export function isMainSplatTarget(
    url: string,
    primaryUrl: string,
    mainFiles: readonly (string | null | undefined)[],
    swaps: readonly SplatSwapPoint[],
): boolean {
    if (url === ORIGINAL_SPLAT_URL || url === primaryUrl) return true;
    if (!url || swaps.some((s) => s?.url === url)) return false;
    return mainFiles.includes(url);
}

/**
 * What a hotspot's or 3D model's "Swap splat on click" (SplatSwapAction) does now:
 * - `original`: go back to the main splat (isMainSplatTarget: the marker, or a file of the main splat).
 * - `swap`: show one of the scene's splat swaps.
 * - `missing`: the target is not one of the scene's splat swaps (deleted): skip it.
 * - null: nothing to do (no action, a malformed one, or its splat is already on screen).
 * `currentUrl` is the splat on screen (a swap's `url`, or the main splat's URL).
 */
export type ClickSplatSwap = { kind: 'original' } | { kind: 'swap'; url: string } | { kind: 'missing'; url: string };

export function resolveClickSplatSwap(
    action: Partial<SplatSwapAction> | null | undefined,
    swaps: readonly SplatSwapPoint[],
    currentUrl: string,
    primaryUrl: string,
    mainFiles: readonly (string | null | undefined)[] = [],
): ClickSplatSwap | null {
    // Read from scene JSON: anything but a non-blank string target is no swap.
    const url = action?.targetSplatUrl;
    if (typeof url !== 'string' || !url.trim()) return null;
    if (isMainSplatTarget(url, primaryUrl, mainFiles, swaps)) return currentUrl === primaryUrl ? null : { kind: 'original' };
    if (!swaps.some((s) => s.url === url)) return { kind: 'missing', url };
    return url === currentUrl ? null : { kind: 'swap', url };
}
